import React from 'react';
import {
  observable,
  action,
  computed,
  toJS,
} from 'mobx';
import debounce from 'lodash/debounce';
import { WebsocketEvent } from '../constants/WebsocketEvent';
import logger from '../utils/logger';
import { ItemType } from '../constants/ItemType';
import { MixTrim } from '../constants/Configuration';
import { removeDuplicates } from '../utils/data-manipulation';
import { loadRequests } from '../utils/async';
import { randomId } from '../utils/random-id';
import { MixDesignConstituent } from '../constants/MixDesignConstituent';
import { MixItem } from '../constants/MixItem';

class MixStore {
  @observable
    mixes = [];
  @observable
    extra = [];
  @observable
    allMixes = [];
  @observable
    externalMixes = [];
  @observable
    allExtra = [];
  @observable
    mixDesigns = [];
  @observable
    mixDesignsObject = {};
  @observable
    mixDesignOffline = [];
  @observable
    mixDesignsWithTrim = [];
  @observable
    loading = false;
  @observable
    saving = false;
  @observable
    checked = new Map();
  @observable
    selectedId = null; // indicates last selected row
  @observable
    selectedExtraId = null;
  @observable
    adjustedMixDesigns = [];
  @observable
    selectedAdjustedProductId = null;
  @observable
    isLoadCompleted = false;

  constructor(rootStore) {
    this.rootStore = rootStore;
    this.debouncedSaveOffline = debounce(this.saveOffline, 0);
  }

  get api() {
    return this.rootStore.newAuthStore;
  }

  get locationId() {
    return this.rootStore.locationsStore.locationId;
  }

  get plantId() {
    return this.rootStore.locationsStore.plantId;
  }

  get timezone() {
    return this.rootStore.scheduleStore.timezone;
  }

  get randomId() {
    return randomId();
  }

  get today() {
    return Date.now();
  }

  /**
   * Empty option for mix dropdown
   * @returns *
   */
  get emptyOption() {
    return {
      key: -1,
      text: '-',
      value: '',
    };
  }

  get mixOptions() {
    const { isOnline } = this.rootStore.connectionStore;

    const data = !isOnline
      ? [ ...this.allMixes, ...this.mixes, ...this.externalMixes ]
      : [ ...this.allMixes, ...this.externalMixes ];

    return this.getOptions(data, {
      key: 'itemId',
      text: ['itemCode', 'itemDescription'],
      value: 'itemId',
      subheader: 'itemCode',
      description: 'itemDescription',
    });
  }

  get extraOptions() {
    const { isOnline } = this.rootStore.connectionStore;

    const data = !isOnline ? [ ...this.allExtra, ...this.extra ] : this.allExtra;
    return this.getOptions(data, {
      key: 'itemId',
      text: 'itemCode',
      value: 'itemId',
    });
  }

  getOptions(data = [], keys) {
    const options = data.map(v => {
      return {
        key: keys ? v[keys.key] : v.id,
        text: keys ? Array.isArray(keys.text) ? `${v[keys.text[0]]} ${v[keys.text[1]]}`: v[keys.text] : v.name || v.code,
        value: keys ? v[keys.value] : v.id,
        content: (
          <span className={`option-content ${keys && keys.className ? keys.className : ''}`}>
            {keys && keys.subheader
              ? <span className="option-subheader">{v[keys.subheader]}</span>
              : <span className="option-text">{keys ? Array.isArray(keys.text) ? `${v[keys.text[0]]} ${v[keys.text[1]]}`: v[keys.text] : v.name || v.code}</span>
            }
            {keys && keys.description && <span className="selection-description">{v[keys.description]}</span>}
          </span>
        ),
      };
    });

    options.unshift(this.emptyOption);

    return options;
  }

  addExternalMix(externalMix) {
    if (externalMix.itemId) {
      return externalMix;
    }

    const mix = {
      ...externalMix,
      itemId: this.randomId,
    };

    this.externalMixes.push(mix);

    return mix;
  }

  getMixById = id => this.mixes.find(s => s.itemId === id);
  getOnlineMixById = id => this.allMixes.find(s => s.itemId === id);
  getAdjustedMixById = id => this.adjustedMixDesigns.find(s => s.id === id);

  /////////////////////
  // UPDATE RELATED //
  ////////////////////

  @action
    addMix = (mix) => {
      const mixIndex = this.mixes.findIndex(v => v.itemId === mix.itemId);
      if (mixIndex > -1) {
        this.mixes[mixIndex] = mix;
        this.debouncedSaveOffline();
        return Promise.resolve();
      }
      const id = this.randomId;
      this.mixes.push({
        ...mix,
        id: id,
        itemId: id,
      });
      this.debouncedSaveOffline();
      this.selectMix(id);
      return Promise.resolve();
    };

  @action
    deleteMix = id => {
      this.selectedId = null;
      this.mixes = this.mixes.filter(m => Number(m.itemId) !== Number(id));
      this.mixDesignOffline = this.mixDesignOffline.filter(m => Number(m.mixItemId) === Number(id));
      this.checked.set(id, false);
      this.debouncedSaveOffline();
    };

  @action
    cloneMixes = items => {
      items.forEach(item => {
        const mix = this.mixes.find(m => Number(m.itemId) === Number(item.itemId));
        const mixDesign = this.mixDesignOffline.filter(m => Number(m.mixItemId) === Number(item.itemId));
        const newId = this.randomId;

        this.mixes.push({
          id: newId,
          itemId: newId,
          itemCode: item.itemCode,
          itemDescription: mix.itemDescription,
          unitCode: mix.unitCode,
          unitSymbol: mix.unitSymbol,
          unitId: mix.unitId,
          price: mix.price,
          slump: mix.slump,
          airPercent: mix.airPercent,
          waterCementRatio: mix.waterCementRatio,
          maxDropSize: mix.maxDropSize,
          mixingTime: mix.mixingTime,
          taxable: mix.taxable,
          offline: true,
        });
        mixDesign.forEach(m => {
          this.mixDesignOffline.push({
            ...m,
            mixItemId: newId,
          });
        });
      });
      this.debouncedSaveOffline();
    };

  @action
    loadAdjustedMixes = () => {
      const url = `/api/location/${this.locationId}/adjustedMixDesign`;
      this.loadOffline('adjustedMixDesigns', url);

      return this.api
        .makeAuthorizedRequest(url)
        .then(resp => {
          this.adjustedMixDesigns = resp;
        })
        .catch(e => {
          this.rootStore.notifierStore.showError(`${e.message} Couldn't load ${url}, using offline data`);
        });
    };

  @action
    addAdjustedMix = (mix) => {
      return this.api
        .makeAuthorizedRequest(
          `/api/location/${this.locationId}/adjustedMixDesign`,
          mix,
          'POST'
        ).then(() => {
          this.loadAdjustedMixes();
        });
    };

  @action
    updateAdjustedMix = (mix) => {
      return this.api
        .makeAuthorizedRequest(
          `/api/location/${this.locationId}/adjustedMixDesign/${mix.id}`,
          mix,
          'PUT'
        ).then(() => {
          this.loadAdjustedMixes();
        });
    };

  @action
    deleteAdjustedMix = id => {
      return this.api
        .makeAuthorizedRequest(
          `/api/location/${this.locationId}/adjustedMixDesign/${id}`,
          null,
          'DELETE'
        ).then(() => {
          this.loadAdjustedMixes();
        });
    };

  @action
    selectAdjustedProduct = id => {
      this.selectedAdjustedProductId = id;
    };

  @action
    addExtra = (extra) => {
      if (extra.id) {
        this.extra.forEach((v, i) => {
          if (v.id === extra.id) {
            this.extra[i] = extra;
          }
        });
      } else {
        const id = this.randomId;
        this.extra.push({
          id,
          itemId: id,
          ...extra,
        });
        this.selectedExtraId = id;
      }
      this.debouncedSaveOffline();
      return Promise.resolve();
    };

  @action
    deleteExtra = (id) => {
      this.extra = this.extra.filter(v => v.id !== id);
      this.selectedExtraId = null;
      this.debouncedSaveOffline();
    };

  @action
    editExtra = id => {
      this.selectedExtraId = id;
    };

  @action
    getExtraById = id => {
      return this.extra.find(v => v.id === id);
    };

  @action
    getOnlineExtraById = id => {
      return this.allExtra.find(v => v.itemId === id);
    };
  //////////////////////
  // MATERIAL RELATED //
  //////////////////////

  @action
    addMixDesign = (mixDesign, mixItemId) => {
      const mix = this.mixDesignOffline.filter(v => v.mixItemId !== mixItemId);
      this.mixDesignOffline = mix.concat(mixDesign);
      this.selectedId = null;
      this.selectedId = mixItemId;
      this.debouncedSaveOffline();
      return Promise.resolve();
    };

  @action
    print = () => {
      const mixes = this.mixes
        .filter(m => this.checked.get(m.id))
        .map(m => `<tr><td>${m.itemCode}</td><td>${m.itemDescription}</td><tr>`)
        .join('');
      const html = `
      <html>
      <head>
        <style>
        table {
          border-collapse: collapse;
        }
        th, td {
          padding: 5px 10px;
        }

        table, th, td {
          border: 1px solid black;
        }
        </style>
      </head>
      <body>
        <h1>Mix Designs</h1>
        <table>
          <thead>
            <tr><th>Code</th><th>Description</th></tr>
          </thead>
          <tbody>
          ${mixes}
          </tbody>
        </table>
      </body>
      </html>
    `;
      const newWindow = window.open('about:blank');
      newWindow.document.write(html);
      newWindow.print();
    };

  ////////////////////
  // ONLine RELATED //
  ////////////////////

  @action
    loadOnline = () => {
      const requests = [];
      requests.push(this.loadMixes);
      requests.push(this.loadExtra);
      requests.push(this.loadMixDesigns);
      requests.push(this.loadAdjustedMixes);
      requests.push(this.loadMixDesignTrims);

      loadRequests(requests).then(() => {
        this.isLoadCompleted = true;
        logger.log('Load async loop completed');
      });
    };

  loadOffline(key, url) {
    if (!this[key]?.length) {
      return this.api.storeDB.init()
        .then(() => this.api.storeDB.getByKey(url || key))
        .then(data => {
          if (data) {
            this[key] = data;
          }
          return data;
        });
    } else {
      return Promise.resolve(this[key]);
    }
  }

  @action
    loadMixes = () => {
      const url = `/api/plant/${this.plantId}/item/mix/param`;
      this.loadOffline('allMixes', url);

      return this.api
        .makeAuthorizedRequest(url)
        .then(resp => {
          this.allMixes = removeDuplicates(resp, 'itemId');
        })
        .catch(e => {
          this.rootStore.notifierStore.showError(`${e.message} Couldn't load ${url}, using offline data`);
        });
    };

  createMixDesignsObject(mixDesigns = []) {
    if (!Array.isArray(mixDesigns)) {
      return;
    }

    this.mixDesignsObject = {};

    mixDesigns.forEach(v => {
      if (!this.mixDesignsObject[v.mixItemId]) {
        this.mixDesignsObject[v.mixItemId] = [];
      }

      if (!this.mixDesignsObject[v.mixItemId].find(m => m.constituentItemId === v.constituentItemId)) {
        this.mixDesignsObject[v.mixItemId].push(v);
      }
    });
  }

  @action
    loadMixDesigns = () => {
      const url = `/api/location/${this.locationId}/mixDesign`;
      this.loadOffline('mixDesigns', url)
        .then(() => this.createMixDesignsObject(this.mixDesigns));

      return this.api
        .makeAuthorizedRequest(url)
        .then(resp => {
          this.mixDesigns = resp || [];
          this.createMixDesignsObject(this.mixDesigns);
        })
        .catch(e => {
          this.rootStore.notifierStore.showError(`${e.message} Couldn't load ${url}, using offline data`);
        });
    };

  @action
  loadMixDesign(mixItemId) {
    const url = `/api/location/${this.locationId}/item/${mixItemId}/mixDesign`;
    return this.api
      .makeAuthorizedRequest(url)
      .then(resp => {
        if (Array.isArray(resp)) {
          this.mixDesigns = this.mixDesigns.filter(v => v.mixItemId !== mixItemId);
          resp.forEach(r => {
            this.mixDesigns.push({ ...r });
          });
        }

        this.createMixDesignsObject(this.mixDesigns);
      });
  };

  @action
    getMixDesign = (mixItemId) => {
      let mixDesign = [];
      if (mixItemId) {
        mixDesign = this.mixDesignsObject[mixItemId] || [];
        if (!mixDesign.length) {
          mixDesign = this.mixDesignOffline.filter(v => v.mixItemId === mixItemId);
        }
      }

      return computed(() => mixDesign).get();
    };

  @action
    updateMixDesign = (mixDesign, mixItemId) => {
      return this.api
        .makeAuthorizedRequest(
          `/api/location/${this.locationId}/mixItem/${mixItemId}/mixDesign`,
          mixDesign,
          'POST'
        );
    };

  @action
    loadExtra = () => {
      const url = `/api/plant/${this.plantId}/item/extra/param`;
      this.loadOffline('allExtra', url);

      return this.api
        .makeAuthorizedRequest(url)
        .then(resp => {
          this.allExtra = resp;
        });
    };

  @action
    updateExtra = (extra) => {
      return this.api
        .makeAuthorizedRequest(
          `/api/plant/${this.plantId}/item/extra/param`,
          [extra],
          'POST'
        )
        .then(resp => {
          if (Array.isArray(resp)) {
            resp = resp[0];
          }
          this.allExtra.forEach((v, i) => {
            if (resp.itemId === v.itemId) {
              this.allExtra[i] = resp;
            }
          });
        });
    };

  @action
    updateMix = (itemId, slumpTableId) => {
      return this.api
        .makeAuthorizedRequest(
          `/api/plant/${this.plantId}/item/${itemId}/mix/param`,
          { slumpTableId },
          'PUT'
        )
        .then(() => {
          this.allMixes.forEach((v, i) => {
            if (Number(itemId) === Number(v.itemId)) {
              this.allMixes[i].slumpTableId = slumpTableId;
            }
          });
        });
    };

  /////////////////////
  // OFFLINE RELATED //
  /////////////////////

  @action
    saveOffline = () => {
      this.api.storeDB.init().then(() => {
        this.api.storeDB.save('mixes', toJS(this.mixes));
        this.api.storeDB.save('extra', toJS(this.extra));
        this.api.storeDB.save('mixDesignOffline', toJS(this.mixDesignOffline));
      });

    };

  @action
    load = () => {
      this.loadOnline();
      this.loadOffline('mixes');
      this.loadOffline('extra');
      this.loadOffline('mixDesignOffline');
    };

  @action
    loadMixDesignTrims = () => {
      return this.api
        .makeAuthorizedRequest(`/api/batch/mix-designs/trims?plantId=${this.plantId}`)
        .then(resp => {
          this.mixDesignsWithTrim = resp || [];
        });
    };

  @action
    saveMixDesignTrims = (mixDesignTrims) => {
      return this.api
        .makeAuthorizedRequest(
          `/api/batch/mix-designs/trims?plantId=${this.plantId}`,
          [ ...mixDesignTrims ],
          'PUT'
        );
    };

  isDateToday(date) {
    const today = new Date(this.today);
    const d = new Date(date);

    if (isNaN(d?.getDay())) {
      // check legacy date string, this check can be removed after couple weeks
      const dateString = String(date);
      if (dateString.length === 8) {
        const year = dateString.substring(0, 4); // '2025'
        const month = dateString.substring(4, 6); // '12'
        const day = dateString.substring(6, 8); // '02'

        const properDateString = `${year}-${month}-${day}`;

        return this.isDateToday(properDateString);
      }

      logger.warn('invalid date', date);
      return false;
    }

    return d.getUTCDate() === today.getUTCDate()
      && d.getUTCMonth() === today.getUTCMonth()
      && d.getUTCFullYear() === today.getUTCFullYear();
  }

  @action
  getMixDesignTrim(mixId, orderCode = '') {
    const { configuration } = this.rootStore.settingsStore;

    if (configuration.saveMixDesignBy === MixTrim.Mix) {
      return this.mixDesignsWithTrim.filter(v => v.saveMixDesignBy === configuration.saveMixDesignBy && v.mixItemId === mixId);
    } else if (configuration.saveMixDesignBy === MixTrim.Day) {
      return this.mixDesignsWithTrim.filter(v => v.saveMixDesignBy === configuration.saveMixDesignBy && this.isDateToday(v.trimDate));
    } else if (configuration.saveMixDesignBy === MixTrim.Order && orderCode) {
      return this.mixDesignsWithTrim.filter(v => v.saveMixDesignBy === configuration.saveMixDesignBy && v.mixItemId === mixId && String(v.orderCode) === String(orderCode));
    }

    return [];
  }

  @action
  async saveMixDesignsWithTrim(mixDesigns = [], orderCode = '', mixItemId) {
    const { configuration } = this.rootStore.settingsStore;

    if (!configuration.saveMixDesignBy || !mixDesigns.length) {
      return;
    }

    const mixToSave = [];

    mixDesigns.forEach(mixDesign => {
      if (mixDesign.trim || mixDesign.initialQuantity) {
        mixToSave.push({
          constituentItemId: mixDesign.constituentItemId,
          constituentItemCode: mixDesign.constituentItemCode,
          batchCode: mixDesign.batchCode,
          initialQuantity: mixDesign.initialQuantity || mixDesign.quantity,
          quantity: mixDesign.quantity,
          quantityNet: mixDesign.quantity + (mixDesign.trim || 0),
          orderCode,
          mixItemId,
          trim: mixDesign.trim || 0,
          trimDate: this.today,
          saveMixDesignBy: configuration.saveMixDesignBy,
        });
      }
    });

    let mixDesignsWithTrim = this.mixDesignsWithTrim.filter(
      mixDesign => mixDesign.mixItemId !== mixDesigns[0].mixItemId &&
      mixDesign.saveMixDesignBy === configuration.saveMixDesignBy
    );

    if (configuration.saveMixDesignBy !== MixTrim.Order) {
      mixDesignsWithTrim = mixDesignsWithTrim.filter(mixDesign => this.isDateToday(mixDesign.trimDate));
    } else {
      mixDesignsWithTrim = mixDesignsWithTrim.filter(mixDesign => mixDesign.orderCode !== orderCode);
    }

    if (configuration.saveMixDesignBy === MixTrim.Day) {
      mixDesignsWithTrim = mixDesignsWithTrim.filter(
        mixDesignWithTrim => !mixDesigns.find(mixDesign => mixDesign.constituentItemId === mixDesignWithTrim.constituentItemId)
      );
    }

    this.mixDesignsWithTrim = mixDesignsWithTrim.concat(mixToSave);

    this.saveMixDesignTrims(this.mixDesignsWithTrim);
  };

  ////////////////////
  // TOGGLE RELATED //
  ////////////////////

  @computed
  get checkedSize() {
    let size = 0;
    this.checked.forEach(v => {
      if (v) size++;
    });
    return size;
  }

  @computed
  get checkedIDs() {
    const arr = [];
    this.checked.forEach((v, id) => {
      if (v) arr.push(id);
    });
    return arr;
  }

  @action
    selectMix = id => {
      this.selectedId = id;
    };

  @action
    toggleCheck = id => {
      const checked = this.checked.get(id);
      this.checked.set(id, !checked);
    };

  @action
    toggleAll = () => {
      this.mixes.forEach(m => {
        this.checked.set(m.id, !this.checked.get(m.id));
      });
    };

  @action
    checkAll = () => {
      this.mixes.forEach(m => {
        this.checked.set(m.id, true);
      });
    };

  @action
    uncheckAll = () => {
      this.mixes.forEach(m => {
        this.checked.set(m.id, false);
      });
    };

  removeItem(itemId) {
    const { materialStore } = this.rootStore;
    this.mixes = this.mixes.filter(v => v.itemId !== itemId);
    this.allMixes = this.allMixes.filter(v => v.itemId !== itemId);
    this.extra = this.extra.filter(v => v.itemId !== itemId);
    this.allExtra = this.allExtra.filter(v => v.itemId !== itemId);
    materialStore.removeMaterialByItemId(itemId);
  }

  startWebSockets = () => {
    const { notifierStore, websocketStore, connectionStore, materialStore, settingsStore, scheduleStore } = this.rootStore;
    this.ws = websocketStore.connection;
    clearTimeout(this.wsTimer);

    if (!this.ws || !connectionStore.isOnline || !websocketStore.isTrulyConnected) {
      this.wsTimer = setTimeout(() => {
        this.startWebSockets();
      }, 2000);
      return;
    }

    this.ws.on(WebsocketEvent.MIX_PARAM_ADD_UPDATE, resp => {
      logger.log('ws:', WebsocketEvent.MIX_PARAM_ADD_UPDATE, resp);

      if (resp) {
        this.loadMixes();
        if (settingsStore.notificationSettings.mix) {
          notifierStore.showInfo('Mix updated!');
        }
      }
    });

    this.ws.on(WebsocketEvent.EXTRA_PRODUCT_PARAM_ADD_UPDATE, resp => {
      logger.log('ws:', WebsocketEvent.EXTRA_PRODUCT_PARAM_ADD_UPDATE, resp);

      if (resp) {
        this.loadExtra();
        if (settingsStore.notificationSettings.mix) {
          notifierStore.showInfo('Extra product updated!');
        }
      }
    });

    this.ws.on(WebsocketEvent.ADJUSTED_MIX_ADD, resp => {
      logger.log('ws:', WebsocketEvent.ADJUSTED_MIX_ADD, resp);

      if (resp) {
        this.loadAdjustedMixes();
        if (settingsStore.notificationSettings.mix) {
          notifierStore.showInfo('Adjusted Mix Design added!');
        }
      }
    });

    this.ws.on(WebsocketEvent.ADJUSTED_MIX_UPDATE, resp => {
      logger.log('ws:', WebsocketEvent.ADJUSTED_MIX_UPDATE, resp);

      if (resp) {
        this.loadAdjustedMixes();
        if (settingsStore.notificationSettings.mix) {
          notifierStore.showInfo('Adjusted Mix Design updated!');
        }
      }
    });

    this.ws.on(WebsocketEvent.ADJUSTED_MIX_DELETE, resp => {
      logger.log('ws:', WebsocketEvent.ADJUSTED_MIX_DELETE, resp);

      if (resp) {
        this.loadAdjustedMixes();
        if (settingsStore.notificationSettings.mix) {
          notifierStore.showInfo('Adjusted Mix Design deleted!');
        }
      }
    });

    this.ws.on(WebsocketEvent.ITEM_ADD, resp => {
      logger.log('ws:', WebsocketEvent.ITEM_ADD, resp);

      if (resp) {
        if (settingsStore.notificationSettings.item) {
          notifierStore.showInfo({ key: 'Item added!', props: { type: resp.type, code: resp.code } });
        }

        switch (resp.type) {
          case ItemType.MIX:
            this.loadMixDesigns();
            this.loadMixes();
            break;
          case ItemType.MATERIAL:
            materialStore.loadMaterials();
            break;
          case ItemType.EXTRA:
            this.loadExtra();
            break;
          default:
            logger.warn('ws: unknown item type', resp.type, resp);
        }
      }
    });

    this.ws.on(WebsocketEvent.ITEM_UPDATE, resp => {
      logger.log('ws:', WebsocketEvent.ITEM_UPDATE, resp);

      if (resp) {
        if (settingsStore.notificationSettings.item) {
          notifierStore.showInfo({ key: 'Item updated!', props: { type: resp.type, code: resp.code } });
        }

        switch (resp.type) {
          case ItemType.MIX:
            this.loadMixes();
            const oldMixDesign = this.mixDesigns.filter(v => v.mixItemId === resp.id);

            if (Array.isArray(resp.mixDesignConstituentItems)) {
              if (oldMixDesign.length !== resp.mixDesignConstituentItems.length) {
                this.loadMixDesign(resp.id);
              } else {
                this.mixDesigns.forEach((v, i) => {
                  if (v.mixItemId === resp.id) {
                    resp.mixDesignConstituentItems.forEach(c => {
                      if (c.id === v.constituentItemId) {
                        this.mixDesigns[i] = { ...v, ...new MixDesignConstituent(c) };
                      }
                    });
                  }
                });

                this.createMixDesignsObject(this.mixDesigns);
              }
            }

            this.allMixes.forEach((v, i) => {
              if (v.itemId === resp.id) {
                this.allMixes[i] = { ...v, ...new MixItem({ ...v, ...resp }) };
              }
            });

            scheduleStore.loadMixDesignForDispatchTickets(true);
            this.mixDesignsWithTrim = this.mixDesignsWithTrim.filter(v => v.mixItemCode !== resp.code);

            break;
          case ItemType.MATERIAL:
            materialStore.loadMaterials();
            break;
          case ItemType.EXTRA:
            this.loadExtra();
            break;
          default:
            logger.warn('ws: unknown item type', resp.type, resp);
        }
      }
    });

    this.ws.on(WebsocketEvent.ITEM_DELETE, resp => {
      logger.log('ws:', WebsocketEvent.ITEM_DELETE, resp);

      if (resp) {
        this.removeItem(resp.id);
        if (settingsStore.notificationSettings.item) {
          notifierStore.showInfo({ key: 'Item deleted!', props: { id: resp.id } });
        }
      }
    });

    this.ws.onclose(this.startWebSockets);
  };
}

export default MixStore;
